import { notify, TabPanel } from '@/components';
import {
  UpdatePricingMarkupDefaultsArgs,
  useGetPricingSettingsMatrixQuery,
  useUpdateMarkupDefaultsMutation,
} from '@/redux';
import { Col, Divider, Row } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuSelect } from './MenuSelect';
import { PricingSettingsDefaultsForm } from './PricingSettingsDefaultsForm';
import { PricingSettingsListPanel } from './PricingSettingsListPanel';
import { PricingMarkupDefaultsDto } from '@/core';

interface Props {
  communityId: string;
}

function useUpdateDefaultMarkups() {
  const [update] = useUpdateMarkupDefaultsMutation();
  return useCallback(
    (value: UpdatePricingMarkupDefaultsArgs) =>
      update(value)
        .unwrap()
        .then(() => notify.success.t('pricing.settings.saved')),
    [update],
  );
}

const DEFAULT_MARKUPS_DATA: PricingMarkupDefaultsDto = {
  offeredItemConstruction: { value: 0, isOverridden: true },
  offeredItemResident: { value: 0, isOverridden: true },
  specialRequestConstruction: { value: 0, isOverridden: true },
  specialRequestResident: { value: 0, isOverridden: true },
  salesTax: { value: 0, isOverridden: true },
  applyTo: [],
};
export function PricingSettingsListTabPanel(props: Props) {
  const { communityId } = props;
  const { t } = useTranslation();
  const [menuId, setMenuId] = useState<string>();
  const { data } = useGetPricingSettingsMatrixQuery({ communityId, menuId });
  const { data: allMenusData } = useGetPricingSettingsMatrixQuery({
    communityId,
  });
  const value: UpdatePricingMarkupDefaultsArgs = useMemo(
    () => ({
      communityId,
      offeredItemConstructionMarkup:
        data?.id && data?.defaultMarkups.offeredItemConstruction?.isOverridden
          ? data?.defaultMarkups.offeredItemConstruction?.value
          : undefined,
      offeredItemResidentMarkup:
        data?.id && data?.defaultMarkups.offeredItemResident?.isOverridden
          ? data?.defaultMarkups.offeredItemResident?.value
          : undefined,
      specialRequestConstructionMarkup:
        data?.id &&
        data?.defaultMarkups.specialRequestConstruction?.isOverridden
          ? data?.defaultMarkups.specialRequestConstruction?.value
          : undefined,
      specialRequestResidentMarkup:
        data?.id && data?.defaultMarkups.specialRequestResident?.isOverridden
          ? data?.defaultMarkups.specialRequestResident?.value
          : undefined,
      salesTax:
        data?.id && data?.defaultMarkups.salesTax?.isOverridden
          ? data?.defaultMarkups.salesTax?.value
          : undefined,
      applyTo: data?.id ? data?.defaultMarkups?.applyTo : undefined,
      menuId,
    }),
    [
      communityId,
      data?.defaultMarkups?.applyTo,
      data?.defaultMarkups.offeredItemConstruction,
      data?.defaultMarkups.offeredItemResident,
      data?.defaultMarkups.salesTax,
      data?.defaultMarkups.specialRequestConstruction,
      data?.defaultMarkups.specialRequestResident,
      data?.id,
      menuId,
    ],
  );
  const handleSubmit = useUpdateDefaultMarkups();

  return (
    <TabPanel>
      <TabPanel.Title>{t('pricing.settings.title')}</TabPanel.Title>
      <TabPanel.Body>
        {data && (
          <>
            <Row className="mb-2">
              <Col span={16}>
                <Row>
                  <Col span={13}>
                    <MenuSelect
                      communityId={communityId}
                      menuId={menuId}
                      setMenuId={setMenuId}
                    />
                  </Col>
                </Row>
              </Col>
              <Divider />
            </Row>
            <Row>
              <PricingSettingsDefaultsForm
                value={value}
                onSubmit={handleSubmit}
                autoSubmit
                initialMarkups={data?.defaultMarkups}
                defaultMarkups={
                  menuId ? allMenusData?.defaultMarkups : DEFAULT_MARKUPS_DATA
                }
              />
            </Row>
          </>
        )}
        <PricingSettingsListPanel
          className="mt-4"
          communityId={communityId}
          menuId={menuId}
        />
      </TabPanel.Body>
    </TabPanel>
  );
}
